"use client"

import React from 'react'
import { X, Filter } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { useCurrentSearchParams } from '@/shared/hooks/use-current-search-params'
import { VolunteerQueryParamsWithOutPaginationType } from '../schemas/volunteer-query.schema'
import { buildUrl } from '@/shared/lib/utils'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'
import { format } from 'date-fns'
import { roleFilterOptions } from '../constants/options'
import { SEARCH_PARAMS_TAB_KEY } from '../constants/volunteer-tab-links'
import { useRegionOptions } from '@/shared/hooks/use-region-options'

// Helper function to get display label for filter values
const getFilterDisplayLabel = (
    key: string,
    value: string | number | boolean | string[] | number[] | null | undefined,
    regionLabelById?: Map<string, string>
) => {
    // Handle null/undefined values
    if (value === null || value === undefined) {
        return ''
    }

    // Handle array values (for roles / region multiselect)
    if (Array.isArray(value)) {
        if (key === 'roles') {
            const labels = value.map(v => {
                const option = roleFilterOptions.find(opt => opt.value === v)
                return option?.label || v
            })
            return labels.join(', ')
        }
        if (key === 'serveDatesRegionId') {
            return value
                .map((v) => regionLabelById?.get(String(v)) ?? String(v))
                .join(', ')
        }
        return value.join(', ')
    }

    // Convert number to string for processing
    const stringValue = String(value)

    if (key === 'serveDatesRegionId') {
        return stringValue
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean)
            .map((id) => regionLabelById?.get(id) ?? id)
            .join(', ')
    }

    // Handle date values (arrivalDate, departureDate, addedFrom, addedTo)
    if (key === 'arrivalDate' || key === 'departureDate' || key === 'addedFrom' || key === 'addedTo' || key === 'lastActiveAtFrom' || key === 'lastActiveAtTo') {
        try {
            if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
                const [year, month, day] = stringValue.split('-').map(Number)
                const date = new Date(year, month - 1, day)
                if (!isNaN(date.getTime())) {
                    return format(date, 'MMM dd, yyyy')
                }
            } else {
                const date = new Date(stringValue)
                if (!isNaN(date.getTime())) {
                    return format(date, 'MMM dd, yyyy')
                }
            }
        } catch {
            // Invalid date, return as-is
        }
    }

    // Handle status (boolean or string 'true'/'false') - String(true)='true', String(false)='false'
    if (key === 'status') {
        if (stringValue === 'true') return 'Active'
        if (stringValue === 'false') return 'Inactive'
    }

    // For text fields, return as-is
    return stringValue
}

// Helper function to get filter display name
const getFilterDisplayName = (key: string) => {
    const displayNames: Record<string, string> = {
        roles: 'Roles',
        organization: 'Organization/Church/Individual',
        arrivalDate: 'Serve Date',
        departureDate: 'Departure Date',
        addedFrom: 'Added (from)',
        addedTo: 'Added (to)',
        lastActiveAtFrom: 'Last Active (from)',
        lastActiveAtTo: 'Last Active (to)',
        status: 'Status',
        search: 'Search',
        serveDatesRegionId: 'Region',
    }
    return displayNames[key] || key
}

interface VolunteerFilterIndicatorProps {
    summary?: React.ReactNode
}

function VolunteerFilterIndicator({ summary }: VolunteerFilterIndicatorProps) {
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const tabParam = searchParams.get(SEARCH_PARAMS_TAB_KEY)
    const { options: regionOptions } = useRegionOptions(true)
    const regionLabelById = React.useMemo(
        () => new Map(regionOptions.map((option) => [String(option.value), option.label])),
        [regionOptions]
    )
    
    // Get current filter parameters (excluding pagination)
    const currentParams = useCurrentSearchParams<VolunteerQueryParamsWithOutPaginationType>([
        "roles",
        "organization",
        "arrivalDate",
        "departureDate",
        "addedFrom",
        "addedTo",
        "lastActiveAtFrom",
        "lastActiveAtTo",
        "status",
        "search",
        "serveDatesRegionId",
    ])

    // Filter out empty values
    const activeFilters = Object.entries(currentParams).filter(([key, value]) => 
        value !== undefined && 
        value !== null && 
        value !== '' && 
        value !== 'undefined' &&
        !(Array.isArray(value) && value.length === 0) // Also filter out empty arrays
    )

    if (activeFilters.length === 0 && !summary) {
        return null
    }

    const removeFilter = (filterKey: string) => {
        const newParams = { ...currentParams } as Record<string, any>
        delete newParams[filterKey]
        
        const url = buildUrl(
            pathname,
            newParams,
            {
                previousParams: {
                    page: "1",
                    limit: "50",
                    ...(tabParam && { [SEARCH_PARAMS_TAB_KEY]: tabParam }),
                }
            }
        )
        router.push(url)
    }

    const clearAllFilters = () => {
        const url = buildUrl(
            pathname,
            {},
            {
                previousParams: {
                    page: "1",
                    limit: "50",
                    ...(tabParam && { [SEARCH_PARAMS_TAB_KEY]: tabParam }),
                }
            }
        )
        router.push(url)
    }

    return (
        <div className="flex flex-wrap items-center gap-2 mb-4">
            {summary}
            {activeFilters.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Filter className="w-4 h-4" />
                    <span>Active filters:</span>
                </div>
            )}
            
            {activeFilters.map(([key, value]) => (
                <div
                    key={key}
                    className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm"
                >
                    <span className="font-medium">{getFilterDisplayName(key)}:</span>
                    <span>{getFilterDisplayLabel(key, value, regionLabelById)}</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto p-0 ml-1 hover:bg-primary/20"
                        onClick={() => removeFilter(key)}
                    >
                        <X className="w-3 h-3" />
                    </Button>
                </div>
            ))}
            
            {activeFilters.length > 0 && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-xs"
                >
                    Clear all
                </Button>
            )}
        </div>
    )
}

export default VolunteerFilterIndicator
